import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { formatDateTime, erpLabel } from "../lib/format";
import { RefreshCw, CheckCircle2, AlertTriangle, Clock, BadgeCheck } from "lucide-react";

export default function Activity() {
  const [events, setEvents] = useState([]);
  const [running, setRunning] = useState(false);
  const [checkingPayments, setCheckingPayments] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/sync/events", { params: { limit: 100 } });
    setEvents(data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const triggerSync = async () => {
    setRunning(true);
    try {
      await api.post("/sync/run");
      await load();
    } finally {
      setRunning(false);
    }
  };

  const checkPayments = async () => {
    setCheckingPayments(true);
    try {
      await api.post("/sync/check-payments");
      await load();
    } finally {
      setCheckingPayments(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="audit"
        title="Activity Log"
        description="Every pull, push, payment, and failure is recorded here for traceability."
        testid="activity-header"
        actions={
          <>
            <button
              type="button"
              className="btn-outline flex items-center gap-2"
              onClick={checkPayments}
              disabled={checkingPayments}
              data-testid="activity-check-payments"
            >
              <BadgeCheck size={14} className={checkingPayments ? "pulse" : ""} />
              {checkingPayments ? "checking…" : "check payments"}
            </button>
            <button
              type="button"
              className="btn-primary flex items-center gap-2"
              onClick={triggerSync}
              disabled={running}
              data-testid="activity-run-sync"
            >
              <RefreshCw size={14} className={running ? "pulse" : ""} />
              {running ? "running…" : "trigger sync"}
            </button>
          </>
        }
      />

      <div className="p-8">
        <div className="card-flat">
          <div className="px-5 py-3 border-b border-neutral-900 flex items-center justify-between">
            <div className="label-mini" data-testid="activity-count">{events.length} events</div>
            <div className="label-mini">most recent first</div>
          </div>
          <table className="data-table" data-testid="activity-table">
            <thead>
              <tr>
                <th>when</th>
                <th>invoice</th>
                <th>flow</th>
                <th>status</th>
                <th>message</th>
                <th>trigger</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr><td colSpan={6} className="text-center text-neutral-500 py-10">no events yet</td></tr>
              )}
              {events.map((ev) => (
                <tr key={ev.id} data-testid={`activity-row-${ev.id}`}>
                  <td className="font-mono text-xs">{formatDateTime(ev.created_at)}</td>
                  <td>
                    <Link to={`/invoices/${ev.invoice_id}`} className="font-mono underline">
                      {ev.invoice_number}
                    </Link>
                  </td>
                  <td className="text-xs">
                    {erpLabel(ev.source_erp)} <span className="text-neutral-400">→</span> {erpLabel(ev.target_erp)}
                  </td>
                  <td>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                      {ev.status === "synced" && <CheckCircle2 size={12} className="text-green-600" />}
                      {ev.status === "failed" && <AlertTriangle size={12} className="text-red-600" />}
                      {ev.status === "pending" && <Clock size={12} className="text-amber-600" />}
                      {ev.status}
                    </div>
                  </td>
                  <td className="text-xs max-w-md break-words">{ev.message}</td>
                  <td className="text-xs font-mono">{ev.triggered_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
